import {
  createContext, useContext, useEffect, useRef, useState, useCallback,
} from "react";
import { toast } from "sonner";
import { useNotifications } from "@/context/NotificationContext";
import api from "@/lib/api";

const RTCCtx = createContext(null);

const STUN = [{ urls: "stun:stun.l.google.com:19302" }, { urls: "stun:stun1.l.google.com:19302" }];
// Cache ICE server config fetched from the backend (which pulls TURN creds
// from Twilio NTS). Refresh every ~50 min since Twilio NTS tokens expire
// after 1 hour by default.
let iceServersCache = { servers: STUN, fetched_at: 0 };

// ─── RTC debug logging ──────────────────────────────────────────────────
// Enable via `localStorage.setItem('rtc_debug', '1')` or `?rtcDebug=1`.
// When enabled, every WebRTC state transition + signaling event logs to
// the console with a consistent prefix so you can grep call recordings.
//
// iter138: default-ON while we chase the "connected but no audio" bug in
// production. Set `localStorage.setItem('rtc_debug', '0')` to silence.
const _rtcDebug = (() => {
  try {
    if (typeof window === "undefined") return false;
    if (window.location?.search?.includes("rtcDebug=1")) return true;
    if (window.localStorage?.getItem("rtc_debug") === "0") return false;
    return true;  // default enabled
  } catch (_) { return true; }
})();
function rtclog(...args) {
  if (_rtcDebug) console.log("[rtc-debug]", ...args);
}
// iter138: always-on warn — surfaces critical issues even with debug off.
function rtcwarn(...args) {
  console.warn("[rtc]", ...args);
}
function rtcstate(peerId, pc) {
  if (!_rtcDebug) return;
  console.log("[rtc-debug]", peerId, "state:", {
    conn: pc.connectionState,
    ice: pc.iceConnectionState,
    sig: pc.signalingState,
    local: pc.localDescription?.type,
    remote: pc.remoteDescription?.type,
    localTracks: pc.getSenders().length,
    remoteTracks: pc.getReceivers().length,
  });
}

async function getIceServers() {
  const now = Date.now();
  if (now - iceServersCache.fetched_at < 50 * 60 * 1000 && iceServersCache.servers.length) {
    return iceServersCache.servers;
  }
  try {
    const { data } = await (await import("@/lib/api")).default.get("/rtc/ice-servers");
    const servers = Array.isArray(data?.iceServers) ? data.iceServers : STUN;
    iceServersCache = { servers, fetched_at: now };
    // iter138: explicit TURN visibility. Without TURN, users behind
    // symmetric NAT / carrier NAT / corporate firewalls will hit
    // "connected but no audio" because relay candidates never form.
    const hasTurn = servers.some((s) => {
      const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
      return urls.some((u) => typeof u === "string" && u.startsWith("turn"));
    });
    if (!hasTurn) {
      rtcwarn("ICE servers response has NO TURN — restrictive NATs will fail. source=", data?.source, "servers=", servers.length);
    } else {
      console.info("[rtc] ICE servers ready: source=", data?.source, "count=", servers.length, "hasTurn=", hasTurn);
    }
    return servers;
  } catch (e) {
    rtcwarn("ice-servers fetch failed, using STUN fallback:", e?.message);
    return STUN;
  }
}

function wsUrl() {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const protocol = base.startsWith("https") ? "wss" : "ws";
  return base.replace(/^https?/, protocol) + "/api/rtc/ws";
}

// ─── Global singleton audio manager (iter127 + iter128) ──────────────────
// Ringtone & ringback used to be per-hook refs which meant a re-render or
// double-mount produced ghost/duplicate audio. This module-level manager
// guarantees exactly ONE oscillator loop of each kind exists at any moment.
//
// iter128 upgrade: replaced the harsh "old telephone" two-tone burst with a
// modern three-note melody in C-major (E5-A5-C6) that loops continuously
// like a real business phone until we explicitly stop it. Softer envelope
// and lower gain avoid the previous "beep beep" harshness while still
// being clearly audible on a laptop speaker.
const audioManager = (() => {
  let ctx = null;
  const active = { ringtone: null, ringback: null };
  const ensureCtx = () => {
    if (!ctx || ctx.state === "closed") {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctx = new AC();
    }
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    return ctx;
  };
  const stop = (kind) => {
    const rec = active[kind];
    if (!rec) return;
    active[kind] = null;
    try { rec.cancel(); } catch (_) { /* noop */ }
  };
  const stopAll = () => { stop("ringtone"); stop("ringback"); };

  // Play a single 3-note phrase then rest, and repeat forever.
  // pattern: notes[] each { freq, dur } + a gap after the phrase.
  const startMelody = (kind, notes, phraseGap, peakGain) => {
    stop(kind);
    const c = ensureCtx();
    if (!c) return;
    let cancelled = false;
    const master = c.createGain();
    master.gain.value = peakGain;
    master.connect(c.destination);
    const playPhrase = () => {
      if (cancelled) return;
      let t = c.currentTime + 0.02;
      notes.forEach(({ freq, dur }) => {
        const osc = c.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, t);
        const g = c.createGain();
        // Soft attack + release so notes glide rather than click.
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(1, t + 0.04);
        g.gain.setValueAtTime(1, t + dur - 0.08);
        g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        osc.connect(g).connect(master);
        osc.start(t);
        osc.stop(t + dur);
        t += dur;
      });
    };
    playPhrase();
    const phraseLen = notes.reduce((s, n) => s + n.dur, 0);
    const total = (phraseLen + phraseGap) * 1000;
    const iv = setInterval(playPhrase, total);
    active[kind] = {
      cancel: () => {
        cancelled = true;
        clearInterval(iv);
        try { master.gain.setTargetAtTime(0.0001, c.currentTime, 0.05); } catch (_) { /* noop */ }
      },
    };
  };
  return {
    // Recipient ringtone — brighter, more prominent (business handset feel).
    startRingtone: () => startMelody(
      "ringtone",
      [{ freq: 659.25, dur: 0.28 }, { freq: 880.00, dur: 0.28 }, { freq: 1046.5, dur: 0.42 }],
      1.2,
      0.16,
    ),
    // Caller ringback — same melody, softer, longer phrase gap (classic PBX feel).
    startRingback: () => startMelody(
      "ringback",
      [{ freq: 440, dur: 0.5 }, { freq: 480, dur: 0.5 }],
      2.4,
      0.09,
    ),
    stopRingtone: () => stop("ringtone"),
    stopRingback: () => stop("ringback"),
    stopAll,
    unlock: () => { ensureCtx(); },
  };
})();

// One-time audio unlock on any user gesture so ringtones actually play
// on first-load without needing a click on the audio element itself.
if (typeof window !== "undefined") {
  const unlockOnce = () => {
    audioManager.unlock();
    ["click", "keydown", "touchstart"].forEach((ev) =>
      window.removeEventListener(ev, unlockOnce, true)
    );
  };
  ["click", "keydown", "touchstart"].forEach((ev) =>
    window.addEventListener(ev, unlockOnce, true)
  );
}

export function RTCProvider({ children }) {
  const { pushNotification } = useNotifications() || {};
  const pushNotifRef = useRef(pushNotification);
  useEffect(() => { pushNotifRef.current = pushNotification; }, [pushNotification]);

  const [connected, setConnected] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);   // { room_id, title, from_user }
  const [activeRoom, setActiveRoom] = useState(null);       // { room_id, title }
  const [peers, setPeers] = useState({});                   // user_id -> { user, stream, connected }
  const [muted, setMuted] = useState(false);
  const [onHold, setOnHold] = useState(false);
  const [duration, setDuration] = useState(0);
  const [ringing, setRinging] = useState(false);            // caller-side "ringback" state
  const [roomState, setRoomState] = useState("idle");       // idle|joining|connected|reconnecting|ended
  const [endedSummary, setEndedSummary] = useState(null);   // { title, duration, participants, endedBy }
  // iter128: dock ↔ expanded view. Calls start docked so users can keep
  // navigating; clicking Expand opens the full HBF-styled call window.
  const [expanded, setExpanded] = useState(false);

  const wsRef = useRef(null);
  const peersRef = useRef({});       // user_id -> { pc, stream, user }
  const localStreamRef = useRef(null);
  const activeRoomRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const tokenRef = useRef(null);
  const tickRef = useRef(null);
  // Track which room we are currently ringing OUT to (as caller) so we can
  // stop the ringback the moment the server tells us the ring is over.
  const outgoingRingRoomRef = useRef(null);
  const handleSignalRef = useRef(() => {});
  // Local user id — captured from the WS `ready` message. Used to decide
  // "polite" vs "impolite" roles during Perfect Negotiation glare handling
  // (lexicographically smaller id = polite by convention).
  const myUidRef = useRef(null);

  // ---------- WebSocket send ----------
  const sendWs = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }, []);

  // iter147: emit a signaling-lifecycle telemetry event to the server so
  // /api/rtc/pair-timeline/<room_id> can reconstruct the full per-pair
  // handshake without a browser DevTools capture. Fire-and-forget; if
  // the WS isn't open, we silently drop (the local console log via
  // rtclog still preserves the info for a browser-side capture).
  const pairTelemetry = useCallback((peerId, event, extra) => {
    try {
      const room_id = activeRoomRef.current?.room_id;
      if (!room_id || !peerId) return;
      const ok = wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
      if (!ok) return;
      wsRef.current.send(JSON.stringify({
        type: "rtc-telemetry", room_id, peer: peerId, event, extra,
      }));
    } catch (_) { /* noop */ }
  }, []);


  // iter130: wait up to `timeoutMs` for the RTC WebSocket to be OPEN.
  //
  // Without this, `joinRoom` used to fire `sendWs` while the socket was
  // still `CONNECTING` — the send silently failed, the server never saw
  // the user in the room, and the "group call fragmentation" bug hit:
  // some invitees ended up locally-only while others correctly joined
  // the shared room on the server.
  //
  // `connect` is declared further down, so we thread it through a ref
  // to avoid a temporal-dead-zone crash at render time.
  const connectRef = useRef(() => {});
  const waitForWsOpen = useCallback(async (timeoutMs = 4000) => {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) return true;
      if (!ws) { try { connectRef.current?.(); } catch (_) { /* noop */ } }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 120));
    }
    return false;
  }, []);

  const connect = useCallback(async () => {
    let ticket = "";
    try {
      const { data } = await api.post("/auth/ws-ticket");
      ticket = data?.ticket;
    } catch (_) {
      return;
    }
    if (!ticket) return;
    tokenRef.current = ticket;
    const url = `${wsUrl()}?ticket=${encodeURIComponent(ticket)}`;
    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        if (!reconnectTimerRef.current) {
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            api.get("/auth/me").then(() => connect()).catch(() => { /* logged out */ });
          }, 3000);
        }
      };
      ws.onerror = (e) => { console.debug("[rtc-ws] error event:", e?.message || e?.type); };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data && data.type === "session-superseded") {
            try {
              if (typeof window !== "undefined" && !/\/login\b/.test(window.location.pathname)) {
                window.location.replace("/login?superseded=1");
              }
            } catch (_) { /* noop */ }
            return;
          }
          handleSignalRef.current(data);
        } catch (err) {
          console.warn("[rtc-ws] bad message", err);
        }
      };
    } catch (e) {
      console.warn("[rtc-ws] connect failed", e);
    }
  }, []);

  // iter130: expose `connect` to earlier-declared hooks (waitForWsOpen)
  // via a ref so we don't hit the temporal-dead-zone that crashed the
  // entire app on first render after the initial iter130 rollout.
  useEffect(() => { connectRef.current = connect; }, [connect]);

  // ---------- Peer connection helpers ----------
  const removePeer = useCallback((userId) => {
    const entry = peersRef.current[userId];
    if (entry?.pc) {
      try { entry.pc.close(); } catch (_) { /* noop */ }
    }
    delete peersRef.current[userId];
    setPeers((p) => {
      const n = { ...p };
      delete n[userId];
      return n;
    });
  }, []);

  const createPeer = useCallback((remoteUser, isInitiator, iceServersOverride) => {
    // Guard against duplicate PC creation for the same remote user (a
    // stray offer arriving after room-peers already spun up an initiator
    // PC would otherwise create a SECOND connection → double audio, or
    // participant stuck at "Joining…" because tracks land on the wrong pc).
    const existing = peersRef.current[remoteUser.id];
    if (existing?.pc) {
      rtclog(remoteUser.id, "createPeer: reusing existing pc");
      pairTelemetry(remoteUser.id, `createPeer_reused initiator=${isInitiator}`);
      return existing.pc;
    }

    const servers = iceServersOverride && iceServersOverride.length ? iceServersOverride : STUN;
    const pc = new RTCPeerConnection({ iceServers: servers });
    const local = localStreamRef.current;
    pairTelemetry(remoteUser.id, `createPeer initiator=${isInitiator} hasLocalStream=${!!local} tracks=${local ? local.getAudioTracks().length : 0}`);
    // Track count invariant: one addTrack per local track, BEFORE any
    // offer/answer creation, so the initial SDP includes m= sections
    // (fixes "connected but no audio").
    if (local) {
      local.getTracks().forEach((tr) => pc.addTrack(tr, local));
      rtclog(remoteUser.id, "createPeer: added", local.getTracks().length, "local tracks");
    } else {
      // No local media (shouldn't happen — joinRoom awaits ensureLocalStream)
      // but add an explicit recvonly transceiver so remote audio still flows.
      try { pc.addTransceiver("audio", { direction: "recvonly" }); } catch (_) { /* noop */ }
      rtclog(remoteUser.id, "createPeer: NO local stream, added recvonly transceiver");
    }

    // ─── Perfect Negotiation state ───────────────────────────────────
    // Polite peer defers on glare; impolite peer holds its ground.
    // Convention: lexicographically smaller uid is polite.
    const myUid = myUidRef.current || "";
    const polite = myUid && remoteUser.id ? myUid < remoteUser.id : !isInitiator;
    const nego = {
      polite,
      makingOffer: false,
      ignoreOffer: false,
      isSettingRemoteAnswerPending: false,
      pendingIce: [],
    };
    peersRef.current[remoteUser.id] = { pc, user: remoteUser, nego };

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        const cand = typeof e.candidate.toJSON === "function"
          ? e.candidate.toJSON()
          : {
              candidate: e.candidate.candidate,
              sdpMid: e.candidate.sdpMid,
              sdpMLineIndex: e.candidate.sdpMLineIndex,
              usernameFragment: e.candidate.usernameFragment,
            };
        rtclog(remoteUser.id, "onicecandidate → send", e.candidate.type || "?", "protocol=", e.candidate.protocol);
        sendWs({ type: "signal", to: remoteUser.id, payload: { kind: "ice", candidate: cand } });
        nego.iceSent = (nego.iceSent || 0) + 1;
        if (nego.iceSent === 1 || nego.iceSent % 5 === 0) {
          pairTelemetry(remoteUser.id, `ice_sent count=${nego.iceSent}`);
        }
      } else {
        pairTelemetry(remoteUser.id, `ice_gathering_complete sent=${nego.iceSent || 0}`);
        rtclog(remoteUser.id, "ICE gathering complete (null candidate)");
      }
    };
    pc.ontrack = (ev) => {
      const [stream] = ev.streams;
      const tracks = stream?.getTracks() || [];
      rtclog(remoteUser.id, "ontrack fired — stream tracks:", tracks.length,
        "kinds:", tracks.map(t => `${t.kind}(${t.readyState},muted=${t.muted},enabled=${t.enabled})`).join(","));
      // iter138: react to remote-track mute/unmute so we can surface silent-audio
      // pathology (track exists, but browser reports it muted → no packets).
      tracks.forEach((tr) => {
        tr.onmute = () => rtcwarn("remote track MUTED", remoteUser.id, tr.kind);
        tr.onunmute = () => rtclog(remoteUser.id, "remote track unmuted", tr.kind);
        tr.onended = () => rtcwarn("remote track ENDED", remoteUser.id, tr.kind);
      });
      peersRef.current[remoteUser.id] = { ...(peersRef.current[remoteUser.id] || {}), pc, stream, nego };
      setPeers((p) => ({ ...p, [remoteUser.id]: { user: remoteUser, stream, connected: true } }));
    };
    pc.onsignalingstatechange = () => {
      rtcstate(remoteUser.id, pc);
    };

    // Try to restore audio flow on transient ICE drops instead of tearing
    // the whole peer connection down (fixes "connected but no audio").
    let iceRestartTimer = null;
    let audioWatchdog = null;
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      rtclog(remoteUser.id, "iceState=", st);
      if (st === "disconnected") {
        setRoomState((cur) => (cur === "connected" ? "reconnecting" : cur));
        if (iceRestartTimer) return;
        iceRestartTimer = setTimeout(() => {
          iceRestartTimer = null;
          if (pc.iceConnectionState === "disconnected" || pc.iceConnectionState === "failed") {
            // With Perfect Negotiation + onnegotiationneeded wired below,
            // pc.restartIce() will trigger a fresh offer through the same
            // safe path (no glare).
            try { pc.restartIce && pc.restartIce(); rtclog(remoteUser.id, "restartIce invoked"); }
            catch (err) { rtclog(remoteUser.id, "restartIce failed", err); }
          }
        }, 2200);
      } else if (st === "connected" || st === "completed") {
        if (iceRestartTimer) { clearTimeout(iceRestartTimer); iceRestartTimer = null; }
        setRoomState((cur) => (cur === "reconnecting" || cur === "joining" ? "connected" : cur));
      }
    };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      rtclog(remoteUser.id, "connectionState=", st);
      pairTelemetry(remoteUser.id, `pc_state=${st}`);
      if (st === "failed" || st === "closed") {
        if (iceRestartTimer) { clearTimeout(iceRestartTimer); iceRestartTimer = null; }
        if (audioWatchdog) { clearInterval(audioWatchdog); audioWatchdog = null; }
        removePeer(remoteUser.id);
      } else if (st === "connected") {
        // iter138: start "connected but no audio" watchdog. If inbound audio
        // bytes stay at zero for 8 seconds after `connected`, force an ICE
        // restart — this recovers from the pathology where signalingState
        // + iceConnectionState both report success but no RTP flows (usually
        // TURN allocation succeeded but candidate pair is broken).
        if (!audioWatchdog) {
          let lastBytesReceived = 0;
          let stagnantChecks = 0;
          let iceRestartsAttempted = 0;
          audioWatchdog = setInterval(async () => {
            if (pc.connectionState !== "connected") return;
            try {
              const stats = await pc.getStats();
              let inboundBytes = 0;
              let outboundBytes = 0;
              stats.forEach((r) => {
                if (r.type === "inbound-rtp" && r.kind === "audio") inboundBytes += (r.bytesReceived || 0);
                if (r.type === "outbound-rtp" && r.kind === "audio") outboundBytes += (r.bytesSent || 0);
              });
              const delta = inboundBytes - lastBytesReceived;
              rtclog(remoteUser.id, `audio-flow: inBytes=${inboundBytes} (Δ${delta}) outBytes=${outboundBytes}`);
              if (delta > 0) {
                stagnantChecks = 0;
                lastBytesReceived = inboundBytes;
              } else {
                stagnantChecks += 1;
                if (stagnantChecks === 4 && iceRestartsAttempted < 2) {
                  iceRestartsAttempted += 1;
                  rtcwarn("no inbound audio bytes for ~8s on", remoteUser.id, "— triggering restartIce");
                  try { pc.restartIce && pc.restartIce(); } catch (err) { rtcwarn("restartIce failed", err); }
                  stagnantChecks = 0;
                } else if (stagnantChecks >= 8) {
                  rtcwarn("persistent no-audio on", remoteUser.id, "after ICE restarts — giving up watchdog");
                  clearInterval(audioWatchdog);
                  audioWatchdog = null;
                }
              }
            } catch (err) {
              rtclog(remoteUser.id, "getStats failed", err?.message);
            }
          }, 2000);
        }
      }
    };

    // ─── onnegotiationneeded: single funnel for INITIAL offer + ICE restart ─
    // Perfect Negotiation pattern from webrtcHacks. Fires exactly when the
    // pc genuinely needs a new offer (initial track add, restartIce, etc).
    pc.onnegotiationneeded = async () => {
      try {
        nego.makingOffer = true;
        rtclog(remoteUser.id, "negotiationneeded → createOffer");
        await pc.setLocalDescription();  // implicit createOffer since m118+
        pairTelemetry(remoteUser.id, `offer_created type=${pc.localDescription?.type} state=${pc.signalingState}`);
        rtclog(remoteUser.id, "→ send offer");
        sendWs({ type: "signal", to: remoteUser.id, payload: { kind: "offer", sdp: pc.localDescription } });
        pairTelemetry(remoteUser.id, "offer_forwarded");
      } catch (err) {
        pairTelemetry(remoteUser.id, `offer_failed err=${err?.name || err?.message || "?"}`);
        console.warn("[rtc] negotiationneeded failed", err);
      } finally {
        nego.makingOffer = false;
      }
    };

    setPeers((p) => ({ ...p, [remoteUser.id]: { user: remoteUser, stream: null, connected: false } }));

    // Initiator path: rely on onnegotiationneeded (fires automatically as
    // soon as we called addTrack above). This unifies initial offer + ICE
    // restart flows and eliminates the old duplicate-offer race where the
    // initiator's manual createOffer collided with an auto-negotiation
    // trigger.
    if (isInitiator) {
      rtclog(remoteUser.id, "createPeer: initiator role → onnegotiationneeded will drive offer");
    }
    return pc;
  }, [sendWs, removePeer, pairTelemetry]);

  // ---------- Signal handler ----------
  const handleSignal = useCallback(async (data) => {
    const t = data.type;
    if (t === "ready") {
      myUidRef.current = data.user_id || null;
      rtclog("ready — my uid=", myUidRef.current);
      return;
    }

    if (t === "room-ended") {
      toast.error("Meeting has been ended", {
        description: "The last participant left this call. Ask the organizer to start a new one.",
        duration: 6000,
      });
      audioManager.stopAll();
      outgoingRingRoomRef.current = null;
      setRinging(false);
      setIncomingCall(null);
      setActiveRoom(null);
      setRoomState("idle");
      try { window.dispatchEvent(new CustomEvent("hbf:room-ended", { detail: { room_id: data.room_id } })); }
      catch (_) { /* noop */ }
      return;
    }

    if (t === "room-closed") {
      // iter130: authoritative "this room is finished forever" broadcast.
      // Sent by the server to every past-invitee/participant when a room
      // empties. We use it to kill stale Join buttons in chat cards,
      // banners, docks, and any lingering incoming popup.
      audioManager.stopAll();
      // If we were IN this room, tear down locally.
      if (activeRoomRef.current?.room_id === data.room_id) {
        Object.values(peersRef.current).forEach((e) => { try { e.pc?.close(); } catch (_) { /* noop */ } });
        peersRef.current = {};
        setPeers({});
        const ls = localStreamRef.current;
        if (ls) { ls.getTracks().forEach((tr) => tr.stop()); localStreamRef.current = null; }
        activeRoomRef.current = null;
        setActiveRoom(null);
        setRoomState("idle");
        setExpanded(false);
      }
      // If an incoming popup was still up for this room, dismiss it.
      setIncomingCall((cur) => (cur && cur.room_id === data.room_id ? null : cur));
      if (outgoingRingRoomRef.current === data.room_id) {
        outgoingRingRoomRef.current = null;
        setRinging(false);
      }
      try { window.dispatchEvent(new CustomEvent("hbf:room-closed", { detail: { room_id: data.room_id } })); }
      catch (_) { /* noop */ }
      return;
    }

    if (t === "ring-stopped") {
      // Authoritative signal from the server — every ringtone dies here.
      audioManager.stopAll();
      const reason = data.reason || "cancelled";
      // Recipient side: dismiss the incoming popup if it was for this room.
      setIncomingCall((cur) => (cur && cur.room_id === data.room_id ? null : cur));
      // Caller side: flip ringback state off.
      if (outgoingRingRoomRef.current === data.room_id) {
        outgoingRingRoomRef.current = null;
        setRinging(false);
        if (reason === "declined_all") {
          toast.info("Everyone declined the call");
        } else if (reason === "timeout") {
          toast.info("No answer — call timed out");
        }
      }
      return;
    }

    if (t === "coach-reaction") {
      try {
        const e = new CustomEvent("hbf:coach-reaction", { detail: data });
        window.dispatchEvent(e);
      } catch (_) { /* noop */ }
      try {
        toast(`${data.emoji || "✨"} ${data.from_supervisor_name || "Supervisor"}`, {
          description: data.note || "Live reaction from your supervisor",
          duration: 4500,
          position: "top-right",
        });
      } catch (_) { /* noop */ }
      return;
    }

    if (t === "incoming-call") {
      if (activeRoomRef.current?.room_id === data.room_id) return;
      audioManager.startRingtone();
      setIncomingCall({ room_id: data.room_id, title: data.title, from_user: data.from_user });
      try {
        pushNotifRef.current?.({
          id: `call-incoming-${data.room_id}-${Date.now()}`,
          kind: "call",
          title: `Incoming call from ${data.from_user?.name || "Someone"}`,
          body: data.title || "Internal call",
          time: new Date().toISOString(),
          href: `/app/meetings?room=${data.room_id}`,
          read: false,
        });
      } catch (_) { /* noop */ }
      return;
    }

    if (t === "call-declined") {
      toast.info(`${data.from_user?.name || "Someone"} declined`);
      try {
        pushNotifRef.current?.({
          id: `call-declined-${data.room_id}-${Date.now()}`,
          kind: "call",
          title: `${data.from_user?.name || "Someone"} declined your call`,
          body: "Call ended without connecting.",
          time: new Date().toISOString(),
          href: "/app/meetings",
          read: false,
        });
      } catch (_) { /* noop */ }
      return;
    }

    if (t === "meeting-invited") {
      const m = data.meeting || {};
      const when = m.start_at ? new Date(m.start_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "";
      toast(`📅 New meeting: ${m.title || "Untitled"}`, {
        description: `${data.from_user?.name || "Someone"} invited you · ${when}`,
        duration: 8000,
        position: "top-right",
        action: { label: "View", onClick: () => { try { window.location.assign("/app/meetings"); } catch (_) { /* noop */ } } },
      });
      try {
        pushNotifRef.current?.({
          id: `meeting-${m.id || data.room_id || Date.now()}`,
          kind: "meeting",
          title: `Meeting invite: ${m.title || "Untitled"}`,
          body: `${data.from_user?.name || "Someone"} · ${when}`,
          time: new Date().toISOString(),
          href: "/app/meetings",
          read: false,
        });
      } catch (_) { /* noop */ }
      return;
    }

    if (t === "room-peers") {
      const iceServers = await getIceServers();
      // iter142: structured frontend log — pairs with backend `[rtc.join]`
      // for cross-log correlation. Grep prod DevTools for `[rtc.room]`
      // when a participant reports "I don't see everyone."
      console.info(
        "[rtc.room] room-peers received · room_id=", data.room_id,
        "peer_count=", (data.peers || []).length,
        "myUid=", myUidRef.current,
        "peer_ids=", (data.peers || []).map((p) => p.id).join(","),
      );
      // Caller side: entering a room that we rang for means the answerer
      // is already inside — kill the ringback locally as a safety net.
      if (outgoingRingRoomRef.current) {
        audioManager.stopRingback();
        outgoingRingRoomRef.current = null;
        setRinging(false);
      }
      if ((data.peers || []).length > 0) {
        setRoomState("connected");
      }
      for (const u of data.peers || []) {
        createPeer(u, true, iceServers);
      }
      return;
    }

    if (t === "peer-joined") {
      // iter142: log every peer-joined so a joiner who claims to be
      // "invisible" to the rest of the room shows up here (or their
      // absence pinpoints the fragmentation).
      console.info(
        "[rtc.room] peer-joined · room_id=", data.room_id,
        "peer_id=", data.user?.id,
        "peer_name=", data.user?.name,
      );
      // Late joiner arrived in our room. If we are the caller waiting for
      // ringback, they answered → drop ringback immediately.
      if (outgoingRingRoomRef.current === data.room_id) {
        audioManager.stopRingback();
        outgoingRingRoomRef.current = null;
        setRinging(false);
      }
      setRoomState("connected");
      setPeers((p) => ({
        ...p,
        [data.user.id]: { user: data.user, stream: null, connected: false },
      }));
      return;
    }

    if (t === "peer-left") {
      removePeer(data.user_id);
      return;
    }

    if (t === "signal") {
      const from = data.from;
      const payload = data.payload || {};
      const fromUser = data.from_user || { id: from };
      let entry = peersRef.current[from];
      // Auto-create the peer connection on inbound offer if we don't
      // already have one (typical for a late joiner sending offers to
      // us). We flag polite=true here since we're the non-initiating
      // side by default; the polite flag is recomputed in createPeer.
      if (!entry && payload.kind === "offer") {
        const iceServers = await getIceServers();
        createPeer(fromUser, false, iceServers);
        entry = peersRef.current[from];
      }
      const pc = entry?.pc;
      const nego = entry?.nego;
      if (!pc || !nego) return;
      try {
        if (payload.kind === "offer" || payload.kind === "answer") {
          const description = payload.sdp;
          pairTelemetry(from, `${payload.kind}_received sdpType=${description?.type} state=${pc.signalingState}`);
          const readyForOffer = !nego.makingOffer &&
                                (pc.signalingState === "stable" || nego.isSettingRemoteAnswerPending);
          const offerCollision = description.type === "offer" && !readyForOffer;
          nego.ignoreOffer = !nego.polite && offerCollision;
          if (nego.ignoreOffer) {
            pairTelemetry(from, "offer_ignored_impolite");
            rtclog(from, "IGNORING colliding offer (impolite peer)");
            return;
          }
          nego.isSettingRemoteAnswerPending = description.type === "answer";
          rtclog(from, "setRemoteDescription", description.type,
                 "signalingState=", pc.signalingState, "polite=", nego.polite);
          await pc.setRemoteDescription(description);
          pairTelemetry(from, `remote_desc_applied sdpType=${description.type} newState=${pc.signalingState}`);
          nego.isSettingRemoteAnswerPending = false;
          // Drain any ICE candidates that arrived before this description.
          if (nego.pendingIce.length) {
            rtclog(from, "draining", nego.pendingIce.length, "buffered ICE candidates");
            const drained = nego.pendingIce.splice(0);
            pairTelemetry(from, `ice_buffered_drained count=${drained.length}`);
            for (const c of drained) {
              try { await pc.addIceCandidate(c); }
              catch (err) { rtclog(from, "buffered ICE add failed", err); }
            }
          }
          if (description.type === "offer") {
            rtclog(from, "createAnswer");
            await pc.setLocalDescription();  // implicit createAnswer
            pairTelemetry(from, `answer_created type=${pc.localDescription?.type} state=${pc.signalingState}`);
            rtclog(from, "→ send answer");
            sendWs({ type: "signal", to: from, payload: { kind: "answer", sdp: pc.localDescription } });
            pairTelemetry(from, "answer_forwarded");
          }
        } else if (payload.kind === "ice" && payload.candidate) {
          // Buffer ICE candidates that arrive before the remote description
          // is set — otherwise addIceCandidate throws InvalidStateError and
          // the candidate is lost, causing intermittent 3-way connection
          // failures where User C stays on "Joining…" forever.
          if (!pc.remoteDescription) {
            nego.pendingIce.push(payload.candidate);
            pairTelemetry(from, `ice_received_buffered queue=${nego.pendingIce.length}`);
            rtclog(from, "buffering ICE (no remoteDescription yet), queue size=",
                   nego.pendingIce.length);
            return;
          }
          try {
            await pc.addIceCandidate(payload.candidate);
            pairTelemetry(from, "ice_received");
            rtclog(from, "addIceCandidate ok");
          } catch (iceErr) {
            pairTelemetry(from, `ice_add_failed err=${iceErr?.name || iceErr?.message}`);
            if (!nego.ignoreOffer) {
              console.warn("[rtc] addIceCandidate failed", iceErr);
            }
          }
        }
      } catch (err) {
        pairTelemetry(from, `signal_handling_failed err=${err?.name || err?.message}`);
        console.warn("[rtc] signal handling failed", err, {
          from, kind: payload.kind, signalingState: pc.signalingState,
        });
      }
    }
  }, [createPeer, removePeer, sendWs, pairTelemetry]);

  useEffect(() => { handleSignalRef.current = handleSignal; }, [handleSignal]);

  // ---------- Local media + room actions ----------
  const ensureLocalStream = useCallback(async () => {
    // iter138: return cached only if it still has a live audio track. A
    // previous call may have stopped tracks; reusing a dead stream would
    // create peers with no outbound audio.
    const cached = localStreamRef.current;
    if (cached) {
      const live = cached.getAudioTracks().some((t) => t.readyState === "live");
      if (live) return cached;
      rtcwarn("cached local stream has no live audio track — re-acquiring");
      try { cached.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
      localStreamRef.current = null;
    }
    const s = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
    const tracks = s.getAudioTracks();
    if (!tracks.length || tracks[0].readyState !== "live") {
      rtcwarn("getUserMedia returned no live audio track!", tracks.map((t) => t.readyState));
    } else {
      rtclog("local mic ready:", tracks[0].label, "muted=", tracks[0].muted, "enabled=", tracks[0].enabled);
      tracks[0].onmute = () => rtcwarn("LOCAL mic went MUTED (system-level)");
      tracks[0].onunmute = () => rtclog("local mic unmuted");
      tracks[0].onended = () => rtcwarn("LOCAL mic ENDED unexpectedly");
    }
    localStreamRef.current = s;
    return s;
  }, []);

  const joinRoom = useCallback(async ({ room_id, title, invitee_ids }) => {
    if (!room_id) return { ok: false, error: "Missing room_id" };
    // iter142: idempotent client-side. React StrictMode or a rapid
    // second click must not fire two `join-room` messages — that used
    // to cause other participants to see us flicker (peer-left then
    // peer-joined) and momentarily drop audio. If we already have
    // active state for this exact room, no-op.
    if (activeRoomRef.current?.room_id === room_id) {
      rtclog("joinRoom: already in room", room_id, "— idempotent no-op");
      return { ok: true, alreadyIn: true };
    }
    if (!connected) connect();
    try {
      await ensureLocalStream();
    } catch (e) {
      toast.error("Microphone access denied. Allow mic permission and retry.");
      return { ok: false, error: "no-mic" };
    }
    // iter130: gate on WS-OPEN so we never mark the user as "in call"
    // locally while the server has no record of them (root cause of the
    // group-call fragmentation bug).
    const wsReady = await waitForWsOpen(4500);
    if (!wsReady) {
      toast.error("Reconnecting… please try the call again in a moment.");
      return { ok: false, error: "ws-not-ready" };
    }
    activeRoomRef.current = { room_id, title };
    setActiveRoom({ room_id, title });
    setDuration(0);
    setEndedSummary(null);
    setRoomState("joining");
    setExpanded(false);  // Start docked; user clicks Expand to open full view.
    console.info("[rtc] joinRoom → room_id=", room_id, "myUid=", myUidRef.current, "invitees=", (invitee_ids || []).length);
    const joined = sendWs({ type: "join-room", room_id });
    if (!joined) {
      // Belt-and-braces: waitForWsOpen returned true but the socket
      // dropped between poll and send. Abort locally so we don't show a
      // ghost in-call state.
      activeRoomRef.current = null;
      setActiveRoom(null);
      setRoomState("idle");
      toast.error("Connection lost. Try the call again.");
      return { ok: false, error: "ws-send-failed" };
    }
    if (invitee_ids && invitee_ids.length > 0) {
      outgoingRingRoomRef.current = room_id;
      setRinging(true);
      audioManager.startRingback();
      sendWs({ type: "ring", room_id, title, invitee_ids });
    }
    return { ok: true };
  }, [connected, connect, ensureLocalStream, sendWs, waitForWsOpen]);

  const acceptIncoming = useCallback(async () => {
    if (!incomingCall) return;
    audioManager.stopRingtone();
    const ic = incomingCall;
    setIncomingCall(null);
    await joinRoom({ room_id: ic.room_id, title: ic.title || "Call" });
  }, [incomingCall, joinRoom]);

  const declineIncoming = useCallback(() => {
    if (!incomingCall) return;
    audioManager.stopRingtone();
    sendWs({ type: "decline", room_id: incomingCall.room_id, to: incomingCall.from_user?.id });
    setIncomingCall(null);
  }, [incomingCall, sendWs]);

  const cancelOutgoingRing = useCallback(() => {
    const rid = outgoingRingRoomRef.current;
    if (!rid) return;
    sendWs({ type: "ring-cancel", room_id: rid });
    audioManager.stopRingback();
    outgoingRingRoomRef.current = null;
    setRinging(false);
  }, [sendWs]);

  const leaveRoom = useCallback(() => {
    const current = activeRoomRef.current;
    const peerList = Object.values(peersRef.current).map((e) => e.user).filter(Boolean);
    const durationSnapshot = duration;
    // Tell the server so it can tear down participation + ring session.
    sendWs({ type: "leave-room" });
    // Cancel any outgoing ringback we were driving.
    if (outgoingRingRoomRef.current) {
      sendWs({ type: "ring-cancel", room_id: outgoingRingRoomRef.current });
      outgoingRingRoomRef.current = null;
    }
    audioManager.stopAll();
    setRinging(false);
    // Close every RTCPeerConnection + wipe streams.
    Object.values(peersRef.current).forEach((e) => { try { e.pc?.close(); } catch (_) { /* noop */ } });
    peersRef.current = {};
    setPeers({});
    // Stop local mic tracks.
    const ls = localStreamRef.current;
    if (ls) {
      ls.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    setMuted(false);
    // Show a short "Call ended" summary card before returning to the app.
    if (current) {
      setRoomState("ended");
      setEndedSummary({
        title: current.title || "Internal call",
        duration: durationSnapshot,
        participants: peerList,
      });
      setTimeout(() => {
        setEndedSummary(null);
        setRoomState("idle");
      }, 3500);
    } else {
      setRoomState("idle");
    }
    activeRoomRef.current = null;
    setActiveRoom(null);
    setDuration(0);
    setExpanded(false);
    setOnHold(false);
  }, [sendWs, duration]);

  const toggleMuted = useCallback((m) => {
    const next = typeof m === "boolean" ? m : !muted;
    const ls = localStreamRef.current;
    if (ls) {
      ls.getAudioTracks().forEach((t) => { t.enabled = !next; });
    }
    setMuted(next);
  }, [muted]);

  const toggleHold = useCallback(() => {
    // Hold pauses BOTH sending and receiving audio without tearing the peer
    // connection down. Toggling hold flips every audio track's `enabled`.
    setOnHold((prev) => {
      const next = !prev;
      const ls = localStreamRef.current;
      if (ls) ls.getAudioTracks().forEach((t) => { t.enabled = !next && !muted; });
      Object.values(peersRef.current).forEach((entry) => {
        const remote = entry?.stream;
        if (remote) remote.getAudioTracks().forEach((t) => { t.enabled = !next; });
      });
      return next;
    });
  }, [muted]);

  const expandCall    = useCallback(() => setExpanded(true),  []);
  const minimizeCall  = useCallback(() => setExpanded(false), []);

  // ---------- Effects ----------
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        await api.get("/auth/me");
        if (!cancel) connect();
      } catch (_) { /* not authed */ }
    })();
    return () => {
      cancel = true;
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      try { wsRef.current?.close(); } catch (e) { console.debug("[rtc-ws] close on unmount:", e?.message); }
      audioManager.stopAll();
    };
  }, [connect]);

  useEffect(() => {
    function onAuthChange() {
      api.get("/auth/me").then(() => {
        if (!wsRef.current) connect();
      }).catch(() => {
        try { wsRef.current?.close(); } catch (e) { console.debug("[rtc-ws] close on logout:", e?.message); }
        wsRef.current = null;
        audioManager.stopAll();
      });
    }
    window.addEventListener("hbf:auth-change", onAuthChange);
    return () => window.removeEventListener("hbf:auth-change", onAuthChange);
  }, [connect]);

  // Duration ticker (drives the call timer)
  useEffect(() => {
    if (activeRoom) {
      tickRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
      return () => clearInterval(tickRef.current);
    }
  }, [activeRoom]);

  // Missed-call timer: if an incoming call goes unanswered for 30 s, mark it
  // missed AND stop the ringtone. Server also fires ring-stopped(timeout).
  useEffect(() => {
    if (!incomingCall) return;
    const ic = incomingCall;
    const id = setTimeout(() => {
      setIncomingCall((cur) => {
        if (cur && cur.room_id === ic.room_id) {
          try {
            pushNotifRef.current?.({
              id: `missed-${ic.room_id}-${Date.now()}`,
              kind: "call",
              title: `Missed call from ${ic.from_user?.name || "Someone"}`,
              body: ic.title || "Internal call",
              time: new Date().toISOString(),
              href: "/app/meetings",
              read: false,
            });
            audioManager.stopRingtone();
          } catch (_) { /* noop */ }
          return null;
        }
        return cur;
      });
    }, 30000);
    return () => clearTimeout(id);
  }, [incomingCall]);

  const value = {
    connected, incomingCall, activeRoom, peers, muted, onHold, duration,
    ringing, roomState, endedSummary, expanded,
    joinRoom, leaveRoom, acceptIncoming, declineIncoming, toggleMuted,
    toggleHold, cancelOutgoingRing, expandCall, minimizeCall,
    localStreamRef,
  };

  return (
    <RTCCtx.Provider value={value}>
      {children}
      {/* iter130: persistent audio sink. `<audio srcObject>` MUST live in a
          component that stays mounted while `peers` has streams — otherwise
          minimizing the call (unmounting MeetingRoomModal) silently stops
          playback. This sink hangs off the provider itself so no view
          transition can ever tear the streams down. Hidden via zero-sized
          styles; controls (mute etc.) still target `MediaStreamTrack`. */}
      <PersistentAudioSink peers={peers} />
    </RTCCtx.Provider>
  );
}

function PersistentAudioSink({ peers }) {
  return (
    <div aria-hidden style={{ position: "fixed", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}>
      {Object.values(peers).map((p) =>
        p.stream ? <PersistentAudio key={`sink-${p.user.id}`} stream={p.stream} peerId={p.user.id} /> : null
      )}
    </div>
  );
}

// iter138: browsers block <audio autoplay> whenever the user-gesture
// activation has expired by the time ontrack → play() runs (async gap
// after the "Accept"/"Join" click). The old code swallowed the play()
// promise rejection silently → "connected but no audio". We now:
//   1. Log every play() failure loudly with the reason.
//   2. Register a ONE-SHOT document gesture listener that retries play()
//      on the next user interaction (click/keydown/touchstart).
//   3. Retry on the audio element's own 'canplay' event too.
function PersistentAudio({ stream, peerId }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !stream) return undefined;
    el.srcObject = stream;
    let cleanupGesture = null;
    const tryPlay = (reason) => {
      const p = el.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          console.info("[rtc] remote audio playing for", peerId, "(", reason, ")");
        }).catch((err) => {
          console.warn("[rtc] remote audio play() blocked for", peerId, "reason=", reason, "err=", err?.name, err?.message);
          // Attach a one-shot gesture listener to retry — this is the
          // fix for the autoplay-policy variant of "connected but no audio".
          if (!cleanupGesture) {
            const retry = () => {
              cleanupGesture && cleanupGesture();
              cleanupGesture = null;
              tryPlay("user-gesture-retry");
            };
            const events = ["click", "keydown", "touchstart", "pointerdown"];
            events.forEach((ev) => document.addEventListener(ev, retry, { once: true, capture: true }));
            cleanupGesture = () => {
              events.forEach((ev) => document.removeEventListener(ev, retry, { capture: true }));
            };
          }
        });
      }
    };
    // Also retry when the browser reports the element is ready to play.
    const onCanPlay = () => tryPlay("canplay");
    el.addEventListener("canplay", onCanPlay);
    tryPlay("mount");
    return () => {
      el.removeEventListener("canplay", onCanPlay);
      if (cleanupGesture) cleanupGesture();
      try { el.pause(); } catch (_) { /* noop */ }
      el.srcObject = null;
    };
  }, [stream, peerId]);
  return <audio ref={ref} autoPlay playsInline data-testid={`rtc-remote-audio-${peerId}`} />;
}

export const useRTC = () => useContext(RTCCtx);

export function formatRtcDuration(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}
